export const onlyDigits = (value = '') => String(value).replace(/\D/g, '');

export const formatCpfCnpj = (value = '') => {
    const digits = onlyDigits(value);

    if (digits.length <= 11) {
        return digits
            .replace(/^(\d{3})(\d)/, '$1.$2')
            .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
            .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3-$4');
    }

    return digits
        .replace(/^(\d{2})(\d)/, '$1.$2')
        .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3/$4')
        .replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d)/, '$1.$2.$3/$4-$5');
};

/**
 * Formata um valor em real brasileiro
 * @param value
 * @returns {string}
 */
export const formatCurrencyBR = (value, showCurrency = true) => {
    if (value === '' || value == null) return '';

    const formatNumber = (amount, fractionDigits = 0) => {
        const options = showCurrency
            ? {style: 'currency', currency: 'BRL'}
            : {
                minimumFractionDigits: fractionDigits,
                maximumFractionDigits: fractionDigits,
            };

        return new Intl.NumberFormat('pt-BR', options).format(amount);
    };

    if (typeof value === 'number') {
        if (Number.isNaN(value)) return '';
        return formatNumber(value, Number.isInteger(value) ? 0 : 2);
    }

    const raw = String(value).trim();
    if (!raw) return '';

    const sanitized = raw.replace(/[^\d,.-]/g, '');
    if (!sanitized) return '';

    if (sanitized.includes(',')) {
        const [integerPart = '', decimalPart = ''] = sanitized.split(',');
        const integerDigits = onlyDigits(integerPart);
        const decimalDigits = onlyDigits(decimalPart).slice(0, 2);
        const amount = Number(`${integerDigits || '0'}.${decimalDigits || '0'}`);

        if (Number.isNaN(amount)) return '';
        if (!showCurrency && sanitized.endsWith(',')) return `${formatNumber(Number(integerDigits || 0))},`;

        return formatNumber(amount, showCurrency ? 2 : decimalDigits.length);
    }

    const dotParts = sanitized.split('.');
    const hasDecimalDot = dotParts.length === 2 && dotParts[1].length > 0 && dotParts[1].length <= 2;

    if (hasDecimalDot) {
        const integerDigits = onlyDigits(dotParts[0]);
        const decimalDigits = onlyDigits(dotParts[1]).slice(0, 2);
        const amount = Number(`${integerDigits || '0'}.${decimalDigits}`);

        if (Number.isNaN(amount)) return '';
        return formatNumber(amount, showCurrency ? 2 : decimalDigits.length);
    }

    const valueAsInteger = onlyDigits(sanitized);
    if (!valueAsInteger) return '';
    return formatNumber(Number(valueAsInteger), 0);
}

/**
 * Formata uma data para o formato brasileiro
 * @param value
 * @returns {string|Date}
 */
export const formatDateBR = (value) => new Intl.DateTimeFormat('pt-BR').format(value);

/**
 * valida se a data passada como parametro é anterior a dataAtual
 * @param value
 * @returns {boolean}
 */
export const isBefore = (value) => value < Date.now();

/**
 * Data ISO (`2026-11-01` ou `2026-11-01T10:00:00`) em `01/11/2026`, sem passar por `Date`: a data do backend é do
 * calendário, e `new Date('2026-11-01')` cai na véspera no fuso do Brasil.
 * @param value
 * @returns {string}
 */
export const formatDataIsoBR = (value) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
    return match ? `${match[3]}/${match[2]}/${match[1]}` : '-';
};

/**
 * Valor em reais, a partir de número ou de texto com ponto decimal (`"2400.00"`, como o faturamento devolve).
 * @param value
 * @returns {string}
 */
export const formatReais = (value) => {
    const amount = Number(value);
    if (value === null || value === undefined || value === '' || Number.isNaN(amount)) return '-';
    return new Intl.NumberFormat('pt-BR', {style: 'currency', currency: 'BRL'}).format(amount);
};
